import React, { useEffect, useState } from "react";
import { SpotCat } from "@/hooks/useSpotCategories";
import { NavigateButton } from "@/components/NavigateButton";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowRight, MapPin, Navigation, Clock, Trees } from "lucide-react";
import { AppHeader } from "@/components/AppHeader";
import { Gallery } from "@/components/Gallery";
import { VenueMap } from "@/components/VenueMap";
import { HeartButton } from "@/components/HeartButton";
import { LinkShareButtons } from "@/components/ShareButtons";
import { SpotReviews } from "@/components/SpotReviews";
import { PlaceEvents } from "@/components/PlaceEvents";
import { api } from "@/lib/api";
import { Spot, spotShareLink } from "@/lib/spots";
import { usePrefs } from "@/contexts/PrefsContext";

export default function SpotDetails() {
  const { id } = useParams();
  const { t, lang } = usePrefs();
  const navigate = useNavigate();
  const loc = useLocation();
  const [s, setS] = useState<Spot | null | false>(null);
  useEffect(() => { api.get(`/public-spots/${id}`).then((r) => setS(r.data)).catch(() => setS(false)); }, [id]);
  const name = s ? (lang === "en" && s.name_en ? s.name_en : s.name_ar) : "";

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />
      <main className="mx-auto max-w-6xl px-4 py-5 pb-32 md:pb-12" data-testid="spot-details-page">
        <button data-testid="spot-details-back-button" onClick={() => (loc.key === "default" ? navigate("/") : navigate(-1))} className="mb-4 h-10 px-4 inline-flex items-center gap-1.5 rounded-full border bg-card text-sm font-bold hover:bg-accent">
          <ArrowRight className="h-4 w-4 ltr:rotate-180" />{t("details.back")}
        </button>
        {s === null && <div className="aspect-[16/9] rounded-[2rem] bg-muted animate-pulse" />}
        {s === false && <p data-testid="spot-not-found" className="text-center text-muted-foreground py-20">{t("details.notFound")}</p>}
        {s && (
          <div className="grid lg:grid-cols-[1.4fr_1fr] gap-8">
            <div className="space-y-6">
              {s.gallery.length ? <Gallery images={s.gallery} alt={name} /> : <div data-testid="spot-no-photos" className="aspect-[16/9] rounded-[2rem] bg-emerald-100 dark:bg-emerald-950 grid place-items-center"><Trees className="h-16 w-16 text-emerald-600/60" /></div>}
              <div className="flex items-start justify-between gap-3">
                <div className="space-y-2 min-w-0">
                  <span className="inline-block rounded-full bg-emerald-600 text-white text-xs font-bold px-3 py-1" data-testid="spot-category"><SpotCat slug={s.category} /></span>
                  <h1 data-testid="spot-name" className="font-display text-3xl sm:text-4xl font-black">{name}</h1>
                  <p className="text-muted-foreground inline-flex items-center gap-1"><MapPin className="h-4 w-4" />{[s.governorate && t(`gov.${s.governorate}`), s.area, s.address].filter(Boolean).join(" · ")}</p>
                </div>
                <HeartButton spotId={s.spot_id} className="!bg-accent [&_svg]:!text-primary shrink-0" />
              </div>
              {s.hours_ar && <p data-testid="spot-hours" className="inline-flex items-center gap-2 rounded-2xl bg-accent px-4 py-3 font-semibold"><Clock className="h-4 w-4" />{s.hours_ar}</p>}
              {s.description_ar && <p data-testid="spot-description" className="whitespace-pre-line leading-relaxed">{s.description_ar}</p>}
              {s.kind !== "venue" && <p className="text-sm text-muted-foreground">{t("spot.noBooking")}</p>}
              {s.kind === "venue" && <PlaceEvents venueId={s.spot_id} />}
              <SpotReviews spot={s} />
            </div>
            <aside className="space-y-4">
              <VenueMap lat={s.lat} lng={s.lng} className="h-72" spot />
              <NavigateButton lat={s.lat} lng={s.lng} name={name} testId="spot-directions" className="h-12 w-full rounded-full bg-primary text-primary-foreground font-bold inline-flex items-center justify-center gap-2">
                <Navigation className="h-4 w-4" />{t("spot.directions")}
              </NavigateButton>
              <LinkShareButtons id="spot-share" link={spotShareLink(s.spot_id)} msg={t("spot.shareMsg").replace("{name}", name)} />
            </aside>
          </div>
        )}
      </main>
    </div>
  );
}
